/* GG 2029 wall gallery: Pinterest-style masonry (keeps curated order left to right), product filters,
   and a full-screen view with a back arrow that returns to the same scroll spot (also the phone's back
   gesture, through history). Without JS the grid falls back to CSS columns. */
if (!customElements.get('gg29-gallery')) {
  customElements.define(
    'gg29-gallery',
    class Gg29Gallery extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('.gg29-gallery__grid');
        this.items = [...this.grid.children];
        this.viewer = this.querySelector('.gg29-viewer');
        // Fixed-position overlays break inside transformed ancestors (Dawn's scroll animations), so move it to <body>.
        if (this.viewer) document.body.append(this.viewer);
        // The homepage strip is a plain row; only the full gallery uses masonry.
        this.isStrip = this.dataset.layout === 'strip';
        if (!this.isStrip) {
          this.classList.add('is-masonry');
          this.layout = this.layout.bind(this);
          this.resizeObserver = new ResizeObserver(this.layout);
          this.resizeObserver.observe(this.grid);
          this.layout();
        }

        if (this.dataset.showFilters === 'true') this.buildFilters();

        this.grid.addEventListener('click', (e) => {
          const open = e.target.closest('.gg29-wall__open');
          if (open) this.openViewer(open.closest('.gg29-wall'));
        });
        this.viewer.querySelector('.gg29-viewer__back').addEventListener('click', () => this.closeViewer());
        this.viewer.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.closeViewer();
        });
        this.onPopState = () => {
          if (!this.viewer.hidden) this.closeViewer({ fromHistory: true });
        };
        window.addEventListener('popstate', this.onPopState);
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.viewer?.remove();
        window.removeEventListener('popstate', this.onPopState);
      }

      /* Masonry: rows are 1px tall, each tile spans rows equal to its height at the current column width. */
      layout() {
        const styles = getComputedStyle(this.grid);
        const colGap = parseFloat(styles.columnGap) || 0;
        const cols = styles.gridTemplateColumns.split(' ').length;
        const colWidth = (this.grid.clientWidth - colGap * (cols - 1)) / cols;
        const first = this.items.find((i) => !i.hidden);
        const gap = first ? parseFloat(getComputedStyle(first).marginBottom) || 0 : 0;
        this.items.forEach((item) => {
          if (item.hidden) return;
          const ratio = parseFloat(item.dataset.ratio) || 1;
          const height = item.classList.contains('gg29-wall--cta') ? Math.max(colWidth / ratio, item.scrollHeight) : colWidth / ratio;
          item.style.gridRowEnd = `span ${Math.max(1, Math.round(height + gap))}`;
        });
      }

      buildFilters() {
        const seen = new Map();
        this.items.forEach((item) => {
          (item.dataset.filterLabels || '').split(';').forEach((pair) => {
            const [key, label] = pair.split('|');
            if (key && label && !seen.has(key)) seen.set(key, label);
          });
        });
        if (seen.size < 2) return;

        const bar = this.querySelector('.gg29-gallery__filters');
        const make = (key, label) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'gg29-chip';
          b.textContent = label;
          b.dataset.filter = key;
          b.setAttribute('aria-pressed', key === '*' ? 'true' : 'false');
          return b;
        };
        bar.append(make('*', this.dataset.allLabel || 'All'));
        const order = (this.dataset.filterOrder || '').split(',').map((x) => x.trim().toLowerCase()).filter(Boolean);
        const rank = (label) => {
          const i = order.indexOf(label.toLowerCase());
          return i === -1 ? order.length : i;
        };
        [...seen].sort((a, b) => rank(a[1]) - rank(b[1])).forEach(([key, label]) => bar.append(make(key, label)));
        bar.hidden = false;

        bar.addEventListener('click', (e) => {
          const chip = e.target.closest('.gg29-chip');
          if (!chip) return;
          bar.querySelectorAll('.gg29-chip').forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
          const key = chip.dataset.filter;
          this.items.forEach((item) => {
            const keys = (item.dataset.filters || '').split(' ');
            item.hidden = !(key === '*' || keys.includes('*') || keys.includes(key));
          });
          if (!this.isStrip) this.layout();
        });
      }

      openViewer(item) {
        if (!item) return;
        this.lastItem = item;
        const img = this.viewer.querySelector('.gg29-viewer__img');
        const tileImg = item.querySelector('img');
        img.src = item.dataset.full || tileImg?.currentSrc || '';
        img.alt = tileImg?.alt || '';
        const info = item.querySelector('.gg29-wall__info');
        this.viewer.querySelector('.gg29-viewer__info').innerHTML = info ? info.innerHTML : '';

        this.scrollY = window.scrollY;
        document.documentElement.classList.add('gg29-viewer-open');
        this.viewer.hidden = false;
        void this.viewer.offsetWidth; // start the fade from the hidden state
        this.viewer.classList.add('is-open');
        history.pushState({ gg29Viewer: true }, '');
        this.viewer.querySelector('.gg29-viewer__back').focus({ preventScroll: true });
      }

      closeViewer({ fromHistory = false } = {}) {
        if (this.viewer.hidden || this.closing) return;
        this.closing = true;
        this.viewer.classList.remove('is-open');
        const done = () => {
          this.viewer.hidden = true;
          this.closing = false;
          document.documentElement.classList.remove('gg29-viewer-open');
          window.scrollTo(0, this.scrollY || 0);
          this.lastItem?.querySelector('.gg29-wall__open')?.focus({ preventScroll: true });
        };
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        reduce ? done() : setTimeout(done, 220);
        if (!fromHistory && history.state && history.state.gg29Viewer) history.back();
      }
    }
  );
}
